import _ from 'lodash';
import moment from 'moment';

import { can } from 'services/can';
import { CreativeRotationMethodMapping } from 'states/resources/ads/business-logic';
import { PermissionsMapping } from 'common/constants/role-intents';
import flags from 'containers/flags/service';

export function isBillingsManagedThroughEF(campaign) {
    return campaign.billing_enabled === true;
}
export function isUnlimitedBudget(campaign) {
    return campaign.billing_enabled && campaign.campaign_budget_enabled === false;
}
export function isLimitedBillings(campaign) {
    return (
        campaign.billing_enabled &&
        campaign.billing_term === 'billable_media_cost_margin' &&
        campaign.campaign_budget_enabled
    );
}
export function isLimitedMediaSpend(campaign) {
    return (
        campaign.billing_enabled &&
        campaign.billing_term === 'billable_media_cost_markup' &&
        campaign.campaign_budget_enabled
    );
}

export function calculateThirdPartyFees(campaign) {
    let total = 0;

    _.each(campaign.third_party_fees, fee => {
        total += fee.fee;
    });

    return total;
}

export function revenueModelToBillingTerm(revenueModel) {
    const mapping = {
        disabled: 'CPM',
        cpmcpc: 'CPM',
        cpm: 'CPM',
        agencyMargin: 'billable_media_cost_margin',
        totalSpendMarkup: 'billable_media_cost_markup',
    };

    // default to CPM
    return mapping[revenueModel] || 'CPM';
}

export const RevenueModelMapping = {
    Disabled: 'disabled',
    CPMCPC: 'cpmcpc',
    AgencyMargin: 'agencyMargin',
    TotalSpendMarkup: 'totalSpendMarkup',
    CPM: 'cpm',
};

export const CampaignInventoryTypeMapping = {
    GuaranteedInventory: 'guaranteed_inventory',
    NonGuaranteedInventory: 'non_guaranteed_inventory',
};

export const CampaignTypeMapping = {
    Standard: 'standard',
    CTV: 'ctv',
    DOOH: 'dooh',
};

export const CampaignAutoBudgetAllocationOptions = {
    CPA: 'cpa',
    CPC: 'cpc',
    CPCV: 'cpcv',
};

export function getRevenueModelDefaults(revenueModel) {
    const mapping = {
        default: {
            revenueModel: null,
            billing_term: 'billable_media_cost_markup',
            billing_enabled: true,
            campaign_budget_enabled: true,
            flightPacingStrategy: 'ad',
            flights: [],
        },
        disabled: {
            revenueModel: RevenueModelMapping.Disabled,
            billing_term: 'billable_media_cost_markup',
            billing_enabled: false,
            campaign_budget_enabled: false,
        },
        cpmcpc: {
            revenueModel: RevenueModelMapping.CPMCPC,
            billing_term: 'billable_media_cost_markup',
            billing_enabled: true,
            campaign_budget_enabled: false,
        },
        cpm: {
            revenueModel: RevenueModelMapping.CPM,
            billing_term: 'billable_media_cost_markup',
            billing_enabled: true,
            campaign_budget_enabled: false,
        },
        totalSpendMarkup: {
            revenueModel: RevenueModelMapping.TotalSpendMarkup,
            billing_term: 'billable_media_cost_markup',
            billing_enabled: true,
            campaign_budget_enabled: true,
        },
        agencyMargin: {
            revenueModel: RevenueModelMapping.AgencyMargin,
            billing_term: 'billable_media_cost_margin',
            billing_enabled: true,
            campaign_budget_enabled: true,
            campaign_max_total_spend_local: 0,
        },
    };

    if (!revenueModel) {
        return mapping.default;
    }

    return {
        ...mapping[revenueModel],
        // common defaults
        billing_rate: 0,
        total_billings_local: 0,
        budget_allocation_method: 'manual',
        flightPacingStrategy: 'ad',
        flights: [],
        campaign_max_total_impressions: 0,
        campaign_primary_pacing: 'spend',
    };
}

export function getFtaDisabledDefaults() {
    return {
        fta_enabled: false,
        fta_lift_version: null,
        fta_location_list: null,
        fta_management_level: null,
    };
}

export function getRevenueModelOptions({ type, inventory_type }) {
    const campaign_type = type;
    const REVENUE_MODEL_OPTIONS = [
        {
            label: 'CPM / CPC',
            value: RevenueModelMapping.CPMCPC,
            disabledUnderConditions: ({ campaign_type, inventory_type }) =>
                [CampaignTypeMapping.CTV, CampaignTypeMapping.DOOH].includes(campaign_type) ||
                CampaignInventoryTypeMapping.GuaranteedInventory === inventory_type,
        },
        {
            label: 'CPM',
            value: RevenueModelMapping.CPM,
            disabledUnderConditions: ({ campaign_type, inventory_type }) =>
                CampaignTypeMapping.Standard === campaign_type &&
                CampaignInventoryTypeMapping.GuaranteedInventory !== inventory_type,
        },
        {
            label: 'Agency margin',
            value: RevenueModelMapping.AgencyMargin,
            disabledUnderConditions: () => false,
        },
        // { label: 'Total cost markup', value: 'totalSpendMarkup' },
        {
            label: 'Disabled',
            value: RevenueModelMapping.Disabled,
            disabledUnderConditions: () => false,
        },
    ];

    return REVENUE_MODEL_OPTIONS.filter(
        option => !option.disabledUnderConditions({ campaign_type, inventory_type })
    ).map(({ label, value }) => ({ label, value }));
}

export function getCampaignInventoryTypeOptions() {
    const result = [
        {
            value: CampaignInventoryTypeMapping.NonGuaranteedInventory,
            label: 'Non-Guaranteed Inventory',
        },
        {
            value: CampaignInventoryTypeMapping.GuaranteedInventory,
            label: 'Guaranteed Inventory',
        },
    ];

    return result;
}

export function isUsingGuaranteedInventoryType({ inventory_type }) {
    return inventory_type === CampaignInventoryTypeMapping.GuaranteedInventory;
}

export function getCampaignTypeOptions() {
    const result = [
        {
            value: CampaignTypeMapping.Standard,
            label: 'Standard (Mobile & Desktop)',
        },
        {
            value: CampaignTypeMapping.CTV,
            label: 'Connected TV',
        },
        {
            value: CampaignTypeMapping.DOOH,
            label: 'Digital Out-of-Home',
        },
    ];

    return result;
}

export function hasConversionsRequiredWarning(campaign) {
    const allConversions = _.concat(
        _.get(campaign, 'conversions', []),
        _.get(campaign, 'advertiserConversions', [])
    );

    const noConversions = allConversions.length === 0;

    return (
        (hasAnyAdsWithCreativeRotationByConversionOptimization(campaign) ||
            isCampaignCpaOptimized(campaign)) &&
        noConversions
    );
}

export function hasAnyAdsWithCreativeRotationByConversionOptimization(campaign) {
    return (
        _.filter(
            campaign.ads,
            ad => ad.rotation_rules.mode === CreativeRotationMethodMapping.OptimizedForConversions
        ) > 0
    );
}

export const REVENUE_MODELS = Object.values(RevenueModelMapping);

export function isCampaignCpaOptimized(campaign) {
    return (
        campaign.automaticBudgetAllocationOptimizationStrategy ===
            CampaignAutoBudgetAllocationOptions.CPA && campaign.budget_allocation_method === 'auto'
    );
}

export function canToggleStatus(campaign) {
    if (campaign.isArchived) {
        return false;
    }

    return can(PermissionsMapping.CAMPAIGN__VIEW_AND_EDIT);
}

export function canEditCampaign(campaign) {
    if (campaign.isArchived && !flags.isEnabled('efb_201_campaign_expiry')) {
        return false;
    }

    return can(PermissionsMapping.CAMPAIGN__VIEW_AND_EDIT);
}

export const CampaignLifecycleStates = {
    PENDING: 'pending',
    STARTED: 'started',
    ENDED: 'ended',
    FINALIZED: 'finalized',
    LOCKED: 'locked',
    LEGACY: 'legacy',
};

export function isCampaignFinalized(campaign) {
    if (!campaign) {
        return false;
    }

    const isCampaignExpiryEnabled = flags.isEnabled('efb_201_campaign_expiry');
    const isCampaignLocked = [
        CampaignLifecycleStates.FINALIZED,
        CampaignLifecycleStates.LOCKED,
        CampaignLifecycleStates.LEGACY,
    ].includes(campaign.lifecycle?.current_state);
    if (isCampaignExpiryEnabled && isCampaignLocked) {
        return true;
    }

    return false;
}

export function isCampaignLocked(campaign) {
    if (!campaign) {
        return false;
    }

    const isCampaignExpiryEnabled = flags.isEnabled('efb_201_campaign_expiry');
    const isCampaignLocked = [
        CampaignLifecycleStates.LOCKED,
        CampaignLifecycleStates.LEGACY,
    ].includes(campaign.lifecycle?.current_state);
    if (isCampaignExpiryEnabled && isCampaignLocked) {
        return true;
    }

    return false;
}

export function isCampaignLockedOrArchived(campaign) {
    if (!campaign) {
        return false;
    }

    const isCampaignExpiryEnabled = flags.isEnabled('efb_201_campaign_expiry');
    if (isCampaignExpiryEnabled) {
        return isCampaignLocked(campaign) || isArchivedCampaign(campaign);
    }
    return false;
}

export function isCampaignFinalizedOrArchived(campaign) {
    if (!campaign) {
        return false;
    }

    const isCampaignExpiryEnabled = flags.isEnabled('efb_201_campaign_expiry');
    if (isCampaignExpiryEnabled) {
        return isCampaignFinalized(campaign) || isArchivedCampaign(campaign);
    }
    return false;
}

export function isCampaignLegacy(campaign) {
    if (!campaign) {
        return false;
    }

    const isCampaignExpiryEnabled = flags.isEnabled('efb_201_campaign_expiry');
    const isCampaignPurged = campaign.lifecycle?.current_state === CampaignLifecycleStates.LEGACY;
    if (isCampaignExpiryEnabled && isCampaignPurged) {
        return true;
    }

    return false;
}

export function canDuplicateCampaign() {
    return can(PermissionsMapping.CAMPAIGN__VIEW_AND_EDIT);
}

export function canArchiveCampaign(campaign) {
    if (campaign.isArchived) {
        return false;
    }

    return can(PermissionsMapping.CAMPAIGN__VIEW_AND_EDIT);
}

export function canCreateAd(campaign) {
    if (campaign.isArchived && !flags.isEnabled('efb_201_campaign_expiry')) {
        return false;
    }

    return can(PermissionsMapping.CAMPAIGN__VIEW_AND_EDIT);
}

export function isArchivedCampaign(campaign) {
    return campaign.isArchived;
}

export function canViewCampaignCreator() {
    return true;
}

export function getCurrentFlight(campaign) {
    return campaign.currentFlight;
}

export function getMostRecentFlight(campaign) {
    const now = moment();

    return _.findLast(campaign.flights, flight => {
        return now.isAfter(flight.end);
    });
}
